import flatpickr from "flatpickr";
import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

type FlatpickrLocaleModule = Record<string, unknown>;
type LocaleLoader = () => Promise<FlatpickrLocaleModule>;
type CustomLocalesMap = Record<string, flatpickr.CustomLocale>;
type CalendarConfig = flatpickr.Options.Options & {
  locale?: string | flatpickr.CustomLocale;
  locales?: CustomLocalesMap;
};

const CALENDAR_EVENTS = {
  change: "coreCalendar:change",
  reset: "coreCalendar:reset",
  setDates: "coreCalendar:setDates",
} as const;

const RANGE_LAYOUT_CLASS = "core-calendar-container--range";

const DEFAULT_LOCALE = "en" as const;
const BUILT_IN_LOCALES = ["en", "es", "pt"] as const;

const FLATPICKR_LOCALE_LOADERS: Record<string, LocaleLoader> = {
  es: () => import("flatpickr/dist/l10n/es.js"),
  pt: () => import("flatpickr/dist/l10n/pt.js"),
};

export class CoreCalendar implements Plugin {
  private instance: flatpickr.Instance | undefined;
  private readonly loadedLocales = new Map<
    string,
    flatpickr.Options.LocaleKey | flatpickr.CustomLocale
  >();
  private readonly onReset = (event: Event): void => {
    const customEvent = event as CustomEvent<{ silent?: boolean }>;
    const silent = customEvent.detail?.silent ?? false;
    this.instance?.clear(!silent);
  };
  private readonly onSetDates = (event: Event): void => {
    const customEvent = event as CustomEvent<{
      dates?: Date | Date[] | string;
      silent?: boolean;
    }>;
    const dates = customEvent.detail?.dates;
    const silent = customEvent.detail?.silent ?? false;

    if (dates && this.instance) {
      const dateFormat = this.instance.config.dateFormat;
      this.instance.setDate(dates, !silent, dateFormat);
    }
  };

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.bindEvents();
    void this.initializeCalendar();
  }

  private bindEvents(): void {
    this.element.addEventListener(CALENDAR_EVENTS.reset, this.onReset);
    this.element.addEventListener(CALENDAR_EVENTS.setDates, this.onSetDates);
  }

  private unbindEvents(): void {
    this.element.removeEventListener(CALENDAR_EVENTS.reset, this.onReset);
    this.element.removeEventListener(CALENDAR_EVENTS.setDates, this.onSetDates);
  }

  private isCustomLocale(value: unknown): value is flatpickr.CustomLocale {
    return (
      typeof value === "object" &&
      value !== null &&
      "weekdays" in value &&
      "months" in value
    );
  }

  private getLocaleCandidates(locale: string): string[] {
    const normalized = locale.trim().toLowerCase().replaceAll("_", "-");
    const localeWithUnderscore = normalized.replaceAll("-", "_");
    const baseLocale = normalized.split("-")[0];

    return Array.from(new Set([normalized, localeWithUnderscore, baseLocale]));
  }

  private extractCustomLocale(
    localeModule: FlatpickrLocaleModule,
  ): flatpickr.CustomLocale | undefined {
    const values = Object.values(localeModule);

    return values.find((value) => this.isCustomLocale(value));
  }

  private getCustomLocaleFromMap(
    localeCandidates: string[],
    customLocales?: CustomLocalesMap,
  ): flatpickr.CustomLocale | undefined {
    if (!customLocales) {
      return undefined;
    }

    for (const localeCandidate of localeCandidates) {
      const customLocale =
        customLocales[localeCandidate] ??
        customLocales[localeCandidate.replaceAll("_", "-")] ??
        customLocales[localeCandidate.replaceAll("-", "_")];

      if (customLocale) {
        return customLocale;
      }
    }

    return undefined;
  }

  private hasBuiltInLocale(localeCandidates: string[]): boolean {
    return localeCandidates.some((localeCandidate) =>
      BUILT_IN_LOCALES.includes(
        localeCandidate as (typeof BUILT_IN_LOCALES)[number],
      ),
    );
  }

  private async loadBuiltInLocale(
    localeCandidates: string[],
    cacheKey: string,
  ): Promise<flatpickr.Options.LocaleKey | flatpickr.CustomLocale> {
    for (const localeCandidate of localeCandidates) {
      const loader =
        FLATPICKR_LOCALE_LOADERS[localeCandidate] ??
        FLATPICKR_LOCALE_LOADERS[localeCandidate.replaceAll("_", "-")];

      if (!loader) {
        continue;
      }

      try {
        const localeModule = await loader();
        const customLocale = this.extractCustomLocale(localeModule);

        if (customLocale) {
          this.loadedLocales.set(cacheKey, customLocale);
          return customLocale;
        }
      } catch {
        continue;
      }
    }

    return DEFAULT_LOCALE;
  }

  private parseConfig(): CalendarConfig {
    if (!this.element.dataset.config) {
      return {};
    }

    try {
      return JSON.parse(this.element.dataset.config) as CalendarConfig;
    } catch {
      console.warn(
        "CoreCalendar: Invalid data-config JSON. Falling back to defaults.",
      );
      return {};
    }
  }

  private syncRangeLayoutClass(): void {
    const isRange = this.instance?.config.mode === "range";
    this.element.classList.toggle(RANGE_LAYOUT_CLASS, Boolean(isRange));
  }

  private applyNavigationIconClasses(instance: flatpickr.Instance): void {
    const nextButtons = instance.calendarContainer.querySelectorAll(
      ".flatpickr-next-month",
    );
    const prevButtons = instance.calendarContainer.querySelectorAll(
      ".flatpickr-prev-month",
    );

    nextButtons.forEach((button) => {
      button.classList.add("dsc-co-icon-arrow-right");
    });

    prevButtons.forEach((button) => {
      button.classList.add("dsc-co-icon-arrow-left");
    });
  }

  private async resolveLocale(
    locale: string | flatpickr.CustomLocale | undefined,
    customLocales?: CustomLocalesMap,
  ): Promise<flatpickr.Options.LocaleKey | flatpickr.CustomLocale> {
    if (this.isCustomLocale(locale)) {
      return locale;
    }

    if (!locale) {
      return DEFAULT_LOCALE;
    }

    const normalizedLocale = locale.toString().trim().toLowerCase();

    if (!normalizedLocale || normalizedLocale === DEFAULT_LOCALE) {
      return DEFAULT_LOCALE;
    }

    const cachedLocale = this.loadedLocales.get(normalizedLocale);

    if (cachedLocale) {
      return cachedLocale;
    }

    const localeCandidates = this.getLocaleCandidates(normalizedLocale);
    const customLocale = this.getCustomLocaleFromMap(
      localeCandidates,
      customLocales,
    );

    if (customLocale) {
      return customLocale;
    }

    if (!this.hasBuiltInLocale(localeCandidates)) {
      return DEFAULT_LOCALE;
    }

    return this.loadBuiltInLocale(localeCandidates, normalizedLocale);
  }

  private async initializeCalendar(): Promise<void> {
    const config = this.parseConfig();

    const configuredLocale =
      config.locale ??
      this.element.dataset.locale ??
      document.documentElement.lang;
    const resolvedLocale = await this.resolveLocale(
      configuredLocale,
      config.locales,
    );

    const targetInput = this.element.querySelector(
      "[data-calendar-target]",
    ) as HTMLElement;

    const defaultConfig: flatpickr.Options.Options = {
      inline: true,
      minDate: "today",
      monthSelectorType: "static",
      onDayCreate: (_dObj, _dStr, _fp, dayElem) => {
        if (dayElem.dateObj < new Date(new Date().setHours(0, 0, 0, 0))) {
          dayElem.classList.add("past-day");
        }
      },
      onReady: (_selectedDates, _dateStr, _instance) => {
        this.applyNavigationIconClasses(_instance);
      },
      onMonthChange: (_selectedDates, _dateStr, _instance) => {
        this.applyNavigationIconClasses(_instance);
      },
      onYearChange: (_selectedDates, _dateStr, _instance) => {
        this.applyNavigationIconClasses(_instance);
      },
      onChange: (selectedDates, dateStr, instance) => {
        this.element.dispatchEvent(
          new CustomEvent(CALENDAR_EVENTS.change, {
            bubbles: true,
            detail: {
              selectedDates,
              dateStr,
              instance,
            },
          }),
        );
      },
      ...config,
      locale: resolvedLocale,
    };

    if (targetInput) {
      this.instance = flatpickr(targetInput, defaultConfig);
      this.syncRangeLayoutClass();
    } else {
      console.warn("CoreCalendar: Missing data-calendar-target input.");
      this.syncRangeLayoutClass();
    }
  }

  destroy(): void {
    this.instance?.destroy();
    this.instance = undefined;
    this.element.classList.remove(RANGE_LAYOUT_CLASS);
    this.unbindEvents();
  }
}

PluginRegistry.register("coreCalendar", CoreCalendar);
